/**
 * Marco común de cada DEMO: barra de regreso, título y el cartel FIJO que dice que es un demo con datos
 * de ejemplo. El cartel no se puede cerrar: nadie debe confundir un demo con una función terminada.
 */
import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft16Regular, Info16Filled } from '@fluentui/react-icons';
import { AppTopBar } from '../AppTopBar.js';
import { brandingProductoActivo } from '../../lib/brandingDelProducto.js';

export const TEXTO_CARTEL = 'Demo con datos de ejemplo — así funcionaría';

export function CartelDemo() {
  const marca = brandingProductoActivo('ci');
  return (
    <div
      role="note"
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        padding: '8px 16px',
        background: `linear-gradient(90deg, ${marca.c1}, ${marca.c2})`,
        color: '#fff',
        fontWeight: 600,
        fontSize: 'var(--fs-300)',
        letterSpacing: 0.2,
      }}
    >
      <Info16Filled />
      {TEXTO_CARTEL}
    </div>
  );
}

export function DemoMarco({
  titulo,
  dolor,
  children,
  sinTopBar,
}: {
  titulo: string;
  dolor?: string;
  children: ReactNode;
  sinTopBar?: boolean;
}) {
  return (
    <div style={{ minHeight: '100vh', background: 'var(--bg-page)', color: 'var(--text-primary)' }}>
      {!sinTopBar && <AppTopBar />}
      <CartelDemo />
      <div style={{ maxWidth: 1440, margin: '0 auto', padding: '16px 24px 48px' }}>
        <Link
          to="/demos"
          style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            color: 'var(--text-secondary)',
            textDecoration: 'none',
            fontSize: 'var(--fs-200)',
          }}
        >
          <ArrowLeft16Regular /> Todos los demos
        </Link>
        <h1 style={{ fontFamily: 'var(--font-display)', fontSize: 'var(--fs-600)', margin: '8px 0 2px' }}>{titulo}</h1>
        {dolor && <p style={{ margin: '0 0 16px', color: 'var(--text-secondary)' }}>{dolor}</p>}
        {children}
      </div>
    </div>
  );
}

/** Tarjeta/panel con el mismo look que los paneles de planos. */
export function Panel({ titulo, children, extra }: { titulo?: ReactNode; children: ReactNode; extra?: ReactNode }) {
  return (
    <section
      style={{
        background: 'var(--bg-surface)',
        border: '1px solid var(--border-subtle)',
        borderRadius: 'var(--radius-md)',
        padding: 14,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        minWidth: 0,
      }}
    >
      {(titulo || extra) && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          {titulo && <h2 style={{ margin: 0, fontSize: 'var(--fs-400)', fontWeight: 600, flex: 1 }}>{titulo}</h2>}
          {extra}
        </div>
      )}
      {children}
    </section>
  );
}

export function NotaEjemplo({ children }: { children: ReactNode }) {
  return <div style={{ fontSize: 'var(--fs-200)', color: 'var(--text-tertiary)' }}>{children}</div>;
}

export const estiloTabla = {
  tabla: { width: '100%', borderCollapse: 'collapse' as const, fontSize: 'var(--fs-200)' },
  th: {
    textAlign: 'left' as const,
    padding: '6px 8px',
    color: 'var(--text-tertiary)',
    fontWeight: 600,
    borderBottom: '1px solid var(--border-subtle)',
    whiteSpace: 'nowrap' as const,
  },
  td: { padding: '6px 8px', borderBottom: '1px solid var(--border-subtle)', verticalAlign: 'top' as const },
  num: {
    padding: '6px 8px',
    borderBottom: '1px solid var(--border-subtle)',
    textAlign: 'right' as const,
    fontVariantNumeric: 'tabular-nums' as const,
    whiteSpace: 'nowrap' as const,
  },
};
